import * as Base64Url from './Base64URL';
import crypto from 'isomorphic-webcrypto';
import { mapJsonWebKey } from './JWK';
export async function decrypt(pKey, data) {
    const result = await crypto.subtle.decrypt('RSA-OAEP', pKey, data);
    return new Uint8Array(result);
}
export async function exportKey(key) {
    const jwk = await crypto.subtle.exportKey('jwk', key);
    return mapJsonWebKey(jwk, Base64Url.toBase64);
}
export async function importJWK(jwk, usage) {
    return crypto.subtle.importKey('jwk', jwk, {
        name: 'RSASSA-PKCS1-v1_5',
        hash: { name: 'SHA-256' }
    }, true, [usage]);
}
export async function generateEncryptDecryptKeys() {
    return crypto.subtle.generateKey({
        name: 'RSA-OAEP',
        modulusLength: 2048,
        publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
        hash: { name: 'SHA-256' }
    }, true, ['encrypt', 'decrypt']);
}
export async function importDecryptionKey(key) {
    const b64URL = mapJsonWebKey(key, Base64Url.fromBase64);
    return crypto.subtle.importKey('jwk', {
        kty: 'RSA',
        e: b64URL.e,
        n: b64URL.n,
        d: b64URL.d,
        p: b64URL.p,
        q: b64URL.q,
        dp: b64URL.dp,
        dq: b64URL.dq,
        qi: b64URL.qi,
        alg: 'RSA-OAEP-256',
        ext: true
    }, {
        name: 'RSA-OAEP',
        hash: { name: 'SHA-256' }
    }, true, ['decrypt']);
}
export async function importEncryptionKey(modulus, exponent) {
    return crypto.subtle.importKey('jwk', {
        kty: 'RSA',
        e: Base64Url.fromBase64(exponent),
        n: Base64Url.fromBase64(modulus),
        alg: 'RSA-OAEP-256',
        ext: true
    }, {
        name: 'RSA-OAEP',
        hash: { name: 'SHA-256' }
    }, true, ['encrypt']);
}
export async function importSignKey(key) {
    const b64URL = mapJsonWebKey(key, Base64Url.fromBase64);
    return crypto.subtle.importKey('jwk', {
        alg: 'RS256',
        d: b64URL.d,
        dp: b64URL.dp,
        dq: b64URL.dq,
        e: b64URL.e,
        ext: true,
        kty: 'RSA',
        n: b64URL.n,
        p: b64URL.p,
        q: b64URL.q,
        qi: b64URL.qi
    }, {
        name: 'RSASSA-PKCS1-v1_5',
        hash: { name: 'SHA-256' }
    }, true, ['sign']);
}
export async function importVerifyKey(modulus, exponent) {
    return crypto.subtle.importKey('jwk', {
        kty: 'RSA',
        e: Base64Url.fromBase64(exponent),
        n: Base64Url.fromBase64(modulus),
        alg: 'RS256',
        ext: true
    }, {
        name: 'RSASSA-PKCS1-v1_5',
        hash: { name: 'SHA-256' }
    }, true, ['verify']);
}
export function jsonWebKeyToXML(key) {
    return `<RSAKeyValue><Modulus>${key.n}</Modulus><Exponent>${key.e}</Exponent></RSAKeyValue>`;
}
export async function sign(key, data) {
    const result = await crypto.subtle.sign({
        name: 'RSASSA-PKCS1-v1_5',
        hash: { name: 'SHA-256' }
    }, key, data);
    return new Uint8Array(result);
}
export async function verify(key, signature, data) {
    return crypto.subtle.verify({
        name: 'RSASSA-PKCS1-v1_5',
        hash: { name: 'SHA-256' }
    }, key, signature, data);
}
function getValueByTagName(str, tagName) {
    const beginTag = `<${tagName}>`;
    const endTag = `</${tagName}`;
    const beginTagIndex = str.indexOf(beginTag);
    const endTagIndex = str.indexOf(endTag);
    return str.substring(beginTagIndex + beginTag.length, endTagIndex);
}
export async function xmlToJsonWebKey(str) {
    const modulus = getValueByTagName(str, 'Modulus');
    const exponent = getValueByTagName(str, 'Exponent');
    return importVerifyKey(modulus, exponent);
}
